import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';

import {
  TpChartDefinition,
  TpDashboardDefinition,
  TpDashboardSectionDefinition,
  TpDashboardHorizontalLayoutDefinition,
  TpDashboardElement,
  TpStaticTextDefinition,
  TpDashboardTabberDefinition,
  TpDashboardVerticalLayoutDefinition,
  DASHBOARDELEMIDS,
  TpDashboardCreationContext,
  TpDashboardUpdateContext,
  TpBitmapDefinition,
  TpActionButtonsDefinition,
  TpDashboardSequenceLayoutDefinition,
  isChartDefinition,
  supportsElemLayoutHints,
  isSequenceLayoutElement,
} from '../dashboard/interfaces';
import {
  createHorizontalLayout,
  createSequenceLayout,
  createStaticText as createStaticTextElement,
  createVerticalLayout,
  getLayoutInHorGroupRelative,
  getLayoutInVertGroupRelative,
  TpElementLayoutHints,
  DEFAULT_LAYOUT_FRACTION,
  DEFAULT_LAYOUT_HEIGHT,
  DEFAULT_VERTICAL_LAYOUT_HEIGHT,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_GALLERY_COLUMN_COUNT,
} from '../dashboard/layout-hints';
import { convertColumnDataToDisplayString, get_guid } from '../helpers/misc';
import { getDataTable, TpChartConfigParams, TpChartDataBinding } from '../charts/chartDataBindings';
import { swapArrayElements } from '../../../data/helpersMisc';
import { UNBOUND_COLUMN, VALTYPES } from '../../../data/interfaces';
import { getColumnInfo, getCanonicalTypeInfo } from '../../../data/analyticsDataSlice';
import {
  TpActiveEnSelectionFilter,
  TpEnSelectionFilterStepCategorical,
  TpEnSelectionFilterTypes,
  TpEnSelectionFilterStep,
} from '../../../data/selection/interfaces';
import { parseEnSelectionFilter, getSelectionItemsMapForFilter } from '../../../data/selection/selectionEnFilters';
import { getUserError } from '../../../components/miscUtils';
import { CATEGORICAL_ORDERS, sortCategories, TpCategoryInfo } from '../helpers/categoricalSorters';
import { getFilteredCategories } from '../helpers/categoricalTools';
import { analyticsDataStorage } from '../../../data/analyticsDataStorage';
import type {
  TpLayoutProperties,
  TpContainerElementDTO,
  TpWidgetDTO,
  TpFilterSchemaDTO,
  TpSectionDTO,
  TpSectionElementDTO,
} from '../../../data/dashboard-template/schemas';
import { DASHBOARD_TEMPLATE_ELEMENT_TYPES as ELEMENT_TYPES } from '../../../data/dashboard-template/constants';

/**
 * Checks if the element is a string reference (widget key in catalog)
 */
export function isStringElement(elem: TpSectionElementDTO): elem is string {
  return typeof elem === 'string';
}

/**
 * Base guard checking if the element is an object (not null, non-primitive)
 */
export function isObjectElement(elem: unknown): elem is Record<string, any> {
  return typeof elem === 'object' && elem !== null;
}

/**
 * Generic type guard that narrows element based on its literal `type` property
 */
export function isTypedElement<K extends string>(
  elem: TpSectionElementDTO,
  typeLiteral: K,
): elem is Extract<TpSectionElementDTO, { type: K }> {
  return isObjectElement(elem) && 'type' in elem && elem.type === typeLiteral;
}

export interface TpDashboardDefData {
  dashboardDefs: { [id: string]: TpDashboardDefinition };
}

const initialState: TpDashboardDefData = {
  dashboardDefs: {},
};

/** Returns a dashboard definition by ID from the slice state. */
export function getDashboardDef(state: TpDashboardDefData, dashboardId: string) {
  return state.dashboardDefs[dashboardId]!;
}

/**
 * Finds all elements in a dashboard definition that match the provided comparison function.
 * @param dashboardDef dashboard definition to search in
 * @param compareFn function to compare elements
 * @returns array of matching elements and their parent IDs
 */
export function findDashboardElements(
  dashboardDef: TpDashboardDefinition,
  compareFn: (elem: TpDashboardElement) => boolean,
): { elem: TpDashboardElement; parentId: string | null }[] {
  const results: { elem: TpDashboardElement; parentId: string | null }[] = [];
  function recursiveFind(elem: TpDashboardElement, parentId: string | null): void {
    if (compareFn(elem)) {
      results.push({ elem, parentId });
    }
    if ('elements' in elem && Array.isArray(elem.elements)) {
      for (const section of elem.elements) {
        if ('id' in elem) {
          recursiveFind(section, elem.id);
        }
      }
    }
  }
  recursiveFind(dashboardDef, null);
  return results;
}

/** Finds one dashboard element and its parent, or resolves an empty ID to the dashboard root. */
export function findDashboardElement(
  dashboardDef: TpDashboardDefinition,
  id: string,
): { elem: TpDashboardElement; parentId: string | null } {
  if (!id) {
    // it refers to the root level: the dashboard itself
    return {
      elem: dashboardDef,
      parentId: null,
    };
  }
  const compareFn = (elem: TpDashboardElement) => 'id' in elem && elem.id === id;
  const elements = findDashboardElements(dashboardDef, compareFn);
  if (elements.length === 1) {
    return elements[0];
  } else if (elements.length > 1) {
    throw `Multiple elements found with id: ${id}`;
  }
  throw `Invalid element: ${id}`;
}

/** Inserts a dashboard definition into the Redux state, creating the map when needed. */
function handleAddNewDashboard(state: TpDashboardDefData, dashboard: TpDashboardDefinition) {
  if (!state.dashboardDefs) {
    state.dashboardDefs = {};
  }
  state.dashboardDefs[dashboard.id] = dashboard;
}

/** Removes a dashboard definition when the requested ID exists. */
function handleRemoveDashboard(state: TpDashboardDefData, dashboardId: string) {
  if (state.dashboardDefs && dashboardId in state.dashboardDefs) {
    delete state.dashboardDefs[dashboardId];
  }
}

/**
 * Provides utility functions for handling dashboard element layout hints and data bindings.
 * - Widgets, bitmaps, and tabbers derive runtime hints from their parent:
 *   a horizontal parent maps YAML `fraction` to `sizeXRelative` and `height`
 *   to `sizeYAbsolute`; a vertical parent maps `fraction` to `sizeYRelative`.
 * - Horizontal layout containers do not retain their own YAML layout properties.
 *   Their rendered width is calculated from child `sizeXRelative` values.
 * - Vertical layout containers use a separate contract: YAML `height` becomes
 *   `sizeYAbsolute`, and their children use `sizeYRelative` fractions.
 * - Tabber children inherit the current horizontal fallback because the tabber
 *   definition does not carry an `align` field.
 * - `getLayoutInHorGroupRelative` and `getLayoutInVertGroupRelative` apply
 *   defaults, so omitted values and explicit default values are not distinguishable
 *   after hydration.
 *
 * In case of dehydration, the parent alignment must be passed for child elements,
 * preserve the dedicated vertical-layout contract. Note that some runtime hint
 * fields may not have a direct YAML equivalent.
 */
export function getElementLayoutHints(
  parentElemDef: TpContainerElementDTO | null,
  layoutProperties: TpLayoutProperties,
): TpElementLayoutHints {
  // Dimensions of that widget are defined by the widget lauout hints, combined with the parent layout:
  //    Horizontal parent layout:
  //      - Width: from fraction of total
  //      - Height: defined by this widget
  //    Vertical parent layout:
  //      - Width: same as parent
  //      - Height: from fractionf of total
  // A vertical parent consumes the fraction as height; all other parents consume it as width.
  // 1. Check if explicit 'align' property exists and equals 'vertical'
  const hasExplicitVerticalAlign = parentElemDef && 'align' in parentElemDef && parentElemDef.align === 'vertical';

  // 2. Check if shorthand layoutVertical type is used
  const isShorthandVerticalLayout =
    parentElemDef && 'type' in parentElemDef && parentElemDef.type === ELEMENT_TYPES.LayoutVertical;

  const isVertical = hasExplicitVerticalAlign || isShorthandVerticalLayout;

  if (isVertical) {
    return getLayoutInVertGroupRelative(layoutProperties?.fraction);
  } else {
    return getLayoutInHorGroupRelative(layoutProperties?.fraction, layoutProperties?.height);
  }
}

// Layout hints in case the parent element is a dashboard ELEMENT instead of a definition
function getElementLayoutHintsForElement(parentElem: TpDashboardElement, layoutProperties: TpLayoutProperties) {
  if (parentElem.elemType === DASHBOARDELEMIDS.VerticalLayout) {
    return getLayoutInVertGroupRelative(layoutProperties?.fraction);
  } else {
    return getLayoutInHorGroupRelative(layoutProperties?.fraction, layoutProperties?.height);
  }
}

// Reverse direction of layout hints to layout properties
function getLayoutPropertiesFromLayoutHints(layoutHints: TpElementLayoutHints): TpLayoutProperties {
  const layoutProps: TpLayoutProperties = {};
  const frac = layoutHints.sizeXRelative;
  const height = layoutHints.sizeYAbsolute;
  if (frac !== undefined) layoutProps.fraction = frac;
  if (height !== undefined) layoutProps.height = height;
  return layoutProps;
}

/** Normalizes shorthand bindings, resolves creation-context variables, and parses an optional widget filter. */
export function parseWidgetDataBinding(
  ctx: TpDashboardCreationContext,
  widgetDefinition: TpWidgetDTO,
): TpChartDataBinding {
  const dataBindingSource = widgetDefinition.dataBinding;
  const aspectBindings: { [id: string]: any } = { ...dataBindingSource.aspectBindings };

  let secondAspectBindings: { [id: string]: any } = {};
  if (dataBindingSource.secondAspectBindings) secondAspectBindings = { ...dataBindingSource.secondAspectBindings };

  // In case the shortcut "aspectId: bindingValue" is used in the yaml, we expand it to the proper syntax
  for (const [aspectId, aspectDef] of Object.entries(aspectBindings)) {
    if (typeof aspectDef === 'string') aspectBindings[aspectId] = { columnId: aspectDef };
  }
  for (const [aspectId, aspectDef] of Object.entries(secondAspectBindings)) {
    if (typeof aspectDef === 'string') secondAspectBindings[aspectId] = { columnId: aspectDef };
  }

  // Chart data bindings may contain variable names, referrering to values that were injected by higher order components (e.g. a gallery of plots over a series of properties)
  // Here we resolve such variable names to their actual values, provided the context
  if (dataBindingSource.aspectBindings) {
    for (const [aspectId, aspectDef] of Object.entries(aspectBindings)) {
      aspectBindings[aspectId] = {
        ...dataBindingSource.aspectBindings[aspectId],
        columnId: resolveContextVariablesInString(ctx, aspectDef.columnId),
      };
    }
  }
  if (dataBindingSource.secondAspectBindings) {
    for (const [aspectId, aspectDef] of Object.entries(secondAspectBindings)) {
      secondAspectBindings[aspectId] = {
        ...dataBindingSource.secondAspectBindings[aspectId],
        columnId: resolveContextVariablesInString(ctx, aspectDef.columnId),
      };
    }
  }

  const binding: TpChartDataBinding = {
    ...dataBindingSource,
    aspectBindings,
    secondAspectBindings,
  };

  if (widgetDefinition.filter) {
    binding.filter = parseWidgetFilter(ctx, binding, widgetDefinition.filter);
  }

  return binding;
}

/** Replaces `$variable` tokens with values supplied by the current template-generation context. */
export function resolveContextVariablesInString(ctx: TpDashboardCreationContext, inputString: string): string {
  if (!inputString) return inputString;
  let outputString = inputString;
  for (const [variableName, variableContent] of Object.entries(ctx.contextVariables)) {
    outputString = outputString.replace('$' + variableName, variableContent);
  }
  return outputString;
}

/** Copies widget config and resolves template titles and descriptions into runtime config keys. */
export function parseWidgetConfig(ctx: TpDashboardCreationContext, widgetDefinition: TpWidgetDTO): TpChartConfigParams {
  let config: TpChartConfigParams = {};
  if (widgetDefinition.config) config = { ...widgetDefinition.config };
  // we pass a custom defined widget title & description via the config params
  if (widgetDefinition.title) {
    config.customTitle = resolveContextVariablesInString(ctx, widgetDefinition.title);
  }
  if (widgetDefinition.description != null) {
    config.customDescription = resolveContextVariablesInString(ctx, widgetDefinition.description);
  }
  return config;
}

/** Converts a template filter into the runtime selection-filter representation for a data table. */
function parseWidgetFilter(
  ctx: TpDashboardCreationContext,
  dataBinding: TpChartDataBinding,
  filterDefinition: TpFilterSchemaDTO,
): TpActiveEnSelectionFilter {
  return parseEnSelectionFilter(ctx.dataContext, dataBinding.dataTableId, filterDefinition);
}

/** Recursively hydrates one template element into its runtime dashboard definition. */
export function createElement(
  ctx: TpDashboardCreationContext,
  parentElemDef: TpContainerElementDTO | null,
  elemDefSource: TpSectionElementDTO,
  widgets: { [key: string]: TpWidgetDTO },
  actions: { [key: string]: any },
  filter?: TpActiveEnSelectionFilter,
): TpDashboardElement {
  // Generators mutate their local definition and context variables, so never mutate the parsed template tree.
  const elemDef = structuredClone(elemDefSource);

  if (isStringElement(elemDef)) {
    // If just a string is provided, it refers to the ID of a defined chart; in that case we define the widget with the chart
    const chartId = elemDef;
    const widget = widgets[chartId];
    if (!widget) throw getUserError(`Widget not found: ${chartId}`);
    const elemLayoutHints = getElementLayoutHints(parentElemDef, widget.layoutProperties);
    const chartDef: TpChartDefinition = {
      elemType: DASHBOARDELEMIDS.Widget,
      id: get_guid(),
      selector: widget.selector ?? chartId, // Use the chartId as a selector to identify the chart in the template
      elemLayoutHints,
      chartType: widget.chartType,
      dataBinding: parseWidgetDataBinding(ctx, widget),
      config: parseWidgetConfig(ctx, widget), // optionally provided
    };
    if (filter) {
      // Prepend the filter to any filter steps defined in the chart
      const existingSteps = structuredClone(chartDef.dataBinding.filter?.steps || []);
      const newFilter = structuredClone(filter);
      newFilter.steps.push(...existingSteps);
      chartDef.dataBinding.filter = newFilter;
    }
    return chartDef;
  }

  if (isObjectElement(elemDef) && elemDef.type == ELEMENT_TYPES.Widget) {
    const widget = elemDef;
    const elemLayoutHints = getElementLayoutHints(parentElemDef, widget.layoutProperties);
    const chartDef: TpChartDefinition = {
      elemType: DASHBOARDELEMIDS.Widget,
      id: get_guid(),
      elemLayoutHints,
      chartType: widget.chartType,
      selector: widget.selector,
      dataBinding: parseWidgetDataBinding(ctx, widget),
      config: parseWidgetConfig(ctx, widget), // optionally provided
    };
    if (filter) {
      // Prepend the filter to any filter steps defined in the chart
      const existingSteps = structuredClone(chartDef.dataBinding.filter?.steps || []);
      const newFilter = structuredClone(filter);
      newFilter.steps.push(...existingSteps);
      chartDef.dataBinding.filter = newFilter;
    }
    return chartDef;
  }

  // We expand some commonly used shortcuts in the config YAML
  let layoutAlign: 'horizontal' | 'vertical' | 'sequence' | undefined = undefined;
  let isLayout = false;

  if (isTypedElement(elemDef, ELEMENT_TYPES.LayoutHorizontal)) {
    isLayout = true;
    layoutAlign = 'horizontal';
  } else if (isTypedElement(elemDef, ELEMENT_TYPES.LayoutVertical)) {
    isLayout = true;
    layoutAlign = 'vertical';
  } else if (isTypedElement(elemDef, ELEMENT_TYPES.LayoutSequence)) {
    isLayout = true;
    layoutAlign = 'sequence';
  } else if (isTypedElement(elemDef, ELEMENT_TYPES.Layout)) {
    isLayout = true;
    layoutAlign = elemDef.align;
  }

  if (isLayout && isObjectElement(elemDef) && 'elements' in elemDef && Array.isArray(elemDef.elements)) {
    const childElements: TpDashboardElement[] = elemDef.elements.map((childElemDef) =>
      createElement(ctx, elemDef, childElemDef, widgets, actions, structuredClone(filter)),
    );
    if (layoutAlign === 'horizontal') {
      // Create a layout that spaces children horizontally.
      // Height is defined by the children
      // Children's individual widths are defined by their fraction
      return createHorizontalLayout(ctx, get_guid(), childElements);
    } else if (layoutAlign === 'sequence') {
      // Create a layout that wraps elements horizontally into sequence rows.
      return createSequenceLayout(ctx, get_guid(), childElements);
    } else {
      // Create a layout that spaces children vertically.
      // Total height is defined by the layout element,
      // Children's individual heights follow from their defined fraction
      const height = elemDef.layoutProperties?.height;
      return createVerticalLayout(ctx, get_guid(), childElements, DEFAULT_LAYOUT_FRACTION, height);
    }
  }
  if (isTypedElement(elemDef, ELEMENT_TYPES.Text)) {
    return createStaticTextElement(ctx, get_guid(), elemDef.content);
  }

  if (isTypedElement(elemDef, ELEMENT_TYPES.Bitmap)) {
    return createBitmapElement(ctx, parentElemDef, elemDef);
  }

  if (isTypedElement(elemDef, ELEMENT_TYPES.Tabber)) {
    return createTabberElement(ctx, parentElemDef, elemDef, widgets, actions, filter);
  }

  if (isTypedElement(elemDef, ELEMENT_TYPES.Gallery)) {
    return createGalleryElement(ctx, parentElemDef, elemDef, widgets, actions, filter);
  }

  if (isTypedElement(elemDef, ELEMENT_TYPES.PairwizeComparisonGallery)) {
    return createPairwizeComparisonGalleryElement(ctx, parentElemDef, elemDef, widgets, actions, filter);
  }

  if (isTypedElement(elemDef, ELEMENT_TYPES.CategoriesGallery)) {
    return createCategoriesGalleryElement(ctx, parentElemDef, elemDef, widgets, actions, filter);
  }

  if (isTypedElement(elemDef, ELEMENT_TYPES.ActionButtons)) {
    return createActionButtonsElement(ctx, parentElemDef, elemDef, actions);
  }

  throw getUserError(`Invalid element type: ${elemDef.type}`);
}

/** Creates a runtime bitmap element and derives its dimensions from the parent layout. */
export function createBitmapElement(
  ctx: TpDashboardCreationContext,
  parentElemDef: TpContainerElementDTO | null,
  elemDef: Extract<TpSectionElementDTO, { type: typeof ELEMENT_TYPES.Bitmap }>,
): TpBitmapDefinition {
  const elemLayoutHints = getElementLayoutHints(parentElemDef, elemDef.layoutProperties);
  return {
    elemType: DASHBOARDELEMIDS.Bitmap,
    id: elemDef.bitmapId,
    description: elemDef.description || '',
    elemLayoutHints,
  };
}

/** Hydrates a tabber and recursively creates each tab's child element. */
function createTabberElement(
  ctx: TpDashboardCreationContext,
  parentElemDef: TpContainerElementDTO | null,
  elemDef: Extract<TpSectionElementDTO, { type: typeof ELEMENT_TYPES.Tabber }>,
  widgets: Record<string, TpWidgetDTO>,
  actions: Record<string, any>,
  filter?: TpActiveEnSelectionFilter,
): TpDashboardTabberDefinition {
  const childElements: TpDashboardElement[] = elemDef.elements.map((childElemDef) =>
    createElement(ctx, elemDef, childElemDef, widgets, actions, structuredClone(filter)),
  );
  // Todo: should this get a default height? Perhaps it should only default to a default fraction.
  const elemLayoutHints = getElementLayoutHints(parentElemDef, elemDef.layoutProperties);
  return {
    elemType: DASHBOARDELEMIDS.Tabber,
    id: get_guid(),
    elemLayoutHints,
    tabTitles: elemDef.titles!,
    elements: childElements,
  };
}

/** Expands a property-group gallery into tabbed rows of generated dashboard elements. */
function createGalleryElement(
  ctx: TpDashboardCreationContext,
  parentElemDef: TpContainerElementDTO | null,
  elemDef: Extract<TpSectionElementDTO, { type: typeof ELEMENT_TYPES.Gallery }>,
  widgets: Record<string, TpWidgetDTO>,
  actions: Record<string, any>,
  filter?: TpActiveEnSelectionFilter,
): TpDashboardTabberDefinition {
  // Creates a gallery of dashboard elements by looping over a set of properties and creating an element for each.
  // The set of properties to loop over is defined by their group id, specified in colInfo.groupId
  const dataTable = getDataTable(ctx.dataContext, elemDef.dataTableId);
  const props = dataTable.columns
    .filter((colInfo) => colInfo.groupId == elemDef.propertyGroupId)
    .map((colInfo) => colInfo.id);
  const columnCount: number = elemDef.columnCount || DEFAULT_GALLERY_COLUMN_COUNT;
  const variable = elemDef.variable; // the name of the variable used in the dashboard creation context to specify the current property in the loop
  if (!variable) throw getUserError(`Gallery element should define a variable`);

  const tabs: TpDashboardTabberDefinition = {
    id: get_guid(),
    elemType: DASHBOARDELEMIDS.Tabber,
    elemLayoutHints: {},
    elements: [],
    tabTitles: [],
  };

  let elemNr = 0;
  let currentTab: TpDashboardVerticalLayoutDefinition;
  let currentRow: TpDashboardHorizontalLayoutDefinition;
  let currentTabElemCount = 0;

  // Gallery elements are grouped into tabs and rows so large property sets remain navigable.
  function startNewTab() {
    finalizeCurrentTabTitle();
    const newTab: TpDashboardVerticalLayoutDefinition = {
      id: get_guid(),
      elemType: DASHBOARDELEMIDS.VerticalLayout,
      elemLayoutHints: {},
      elements: [],
    };
    tabs.elements.push(newTab);
    tabs.tabTitles.push(`${elemNr + 1}-`);
    currentTab = newTab;
    currentTabElemCount = 0;
    startNewRow();
  }

  function finalizeCurrentTabTitle() {
    if (tabs.tabTitles.length > 0) tabs.tabTitles[tabs.tabTitles.length - 1] += `${elemNr}`;
  }

  function startNewRow() {
    currentRow = {
      id: get_guid(),
      elemType: DASHBOARDELEMIDS.HorizontalLayout,
      elemLayoutHints: {},
      elements: [],
    };
    currentTab.elements.push(currentRow);
  }

  function addGalleryElement() {
    if (elemDef.maxCount && currentTabElemCount >= elemDef.maxCount) startNewTab();
    if (currentRow.elements.length >= columnCount) startNewRow();
    currentRow.elements.push(createElement(ctx, elemDef, elemDef.element, widgets, actions, structuredClone(filter)));
    currentTabElemCount++;
    elemNr++;
  }

  startNewTab();

  const galleryCount = props.length;
  for (let galleryIdx = 0; galleryIdx < galleryCount; galleryIdx++) {
    ctx.contextVariables[variable] = props[galleryIdx];
    ctx.contextVariables[`${variable}Name`] = getColumnInfo(dataTable, props[galleryIdx]).name;
    addGalleryElement();
    delete ctx.contextVariables[variable];
    delete ctx.contextVariables[`${variable}Name`];
  }
  finalizeCurrentTabTitle();

  return tabs;
}

/** Expands a gallery into pairwise comparisons for every bounded property pair. */
function createPairwizeComparisonGalleryElement(
  ctx: TpDashboardCreationContext,
  parentElemDef: TpContainerElementDTO | null,
  elemDef: Extract<TpSectionElementDTO, { type: typeof ELEMENT_TYPES.PairwizeComparisonGallery }>,
  widgets: Record<string, TpWidgetDTO>,
  actions: Record<string, any>,
  filter?: TpActiveEnSelectionFilter,
): TpDashboardVerticalLayoutDefinition {
  // Creates a gallery of dashboard elements, by creating plots for each pair of properties in a set of properties
  // The set of properties to loop over is defined by their group id, specified in colInfo.groupId
  const dataTable = getDataTable(ctx.dataContext, elemDef.dataTableId);
  const props = dataTable.columns
    .filter((colInfo) => colInfo.groupId == elemDef.propertyGroupId)
    .map((colInfo) => colInfo.id);
  const variable1 = elemDef.variable1; // the name of the variable used in the dashboard creation context to specify the first comparison property in the loop
  if (!variable1) throw getUserError(`Gallery element should define a variable1`);
  const variable2 = elemDef.variable2; // the name of the variable used in the dashboard creation context to specify the second comparison property in the loop
  if (!variable2) throw getUserError(`Gallery element should define a variable2`);

  let currentChartRow: TpDashboardElement[] = [];
  const chartRows: TpDashboardHorizontalLayoutDefinition[] = [];

  function addChartRow() {
    chartRows.push({
      id: get_guid(),
      elemType: DASHBOARDELEMIDS.HorizontalLayout,
      elemLayoutHints: getLayoutInHorGroupRelative(DEFAULT_LAYOUT_FRACTION, DEFAULT_LAYOUT_HEIGHT),
      elements: currentChartRow,
    });
    currentChartRow = [];
  }

  let propertyCount = props.length;
  if (elemDef.maxCount) propertyCount = Math.min(propertyCount, elemDef.maxCount);
  for (let propertyIdx1 = 1; propertyIdx1 < propertyCount; propertyIdx1++) {
    ctx.contextVariables[variable1] = props[propertyIdx1];
    ctx.contextVariables[`${variable1}Name`] = getColumnInfo(dataTable, props[propertyIdx1]).name;
    for (let propertyIdx2 = 0; propertyIdx2 < propertyIdx1; propertyIdx2++) {
      ctx.contextVariables[variable2] = props[propertyIdx2];
      ctx.contextVariables[`${variable2}Name`] = getColumnInfo(dataTable, props[propertyIdx2]).name;
      currentChartRow.push(createElement(ctx, elemDef, elemDef.element, widgets, actions, structuredClone(filter)));
      delete ctx.contextVariables[variable2];
      delete ctx.contextVariables[`${variable2}Name`];
    }
    delete ctx.contextVariables[variable1];
    delete ctx.contextVariables[`${variable1}Name`];
    addChartRow();
  }

  return {
    id: get_guid(),
    elemType: DASHBOARDELEMIDS.VerticalLayout,
    elemLayoutHints: {},
    elements: chartRows,
  };
}

/** Expands each selected category into a filtered chart, grouped into tabs and rows. */
function createCategoriesGalleryElement(
  ctx: TpDashboardCreationContext,
  parentElemDef: TpContainerElementDTO | null,
  elemDef: Extract<TpSectionElementDTO, { type: typeof ELEMENT_TYPES.CategoriesGallery }>,
  widgets: Record<string, TpWidgetDTO>,
  actions: Record<string, any>,
  activeFilter?: TpActiveEnSelectionFilter,
): TpDashboardTabberDefinition {
  // The set of properties to loop over is defined by their group id, specified in colInfo.groupId
  const dataTable = getDataTable(ctx.dataContext, elemDef.dataTableId);
  const columnId = elemDef.property;
  const variable = elemDef.variable; // the name of the variable used in the dashboard creation context to specify the categorical property state
  if (!variable) throw getUserError(`Gallery element should define a variable`);
  const columnCount: number = elemDef.columnCount || DEFAULT_GALLERY_COLUMN_COUNT;
  const columnInfo = getColumnInfo(dataTable, columnId);
  const previousFilterSteps = activeFilter ? structuredClone(activeFilter).steps : [];

  const singleChartPerTab = columnCount == 1 && elemDef.maxCount == 1; // if true, a tab contains exactly one chart with one categorical state, and we use that state as a tab name

  if (
    (columnInfo.valueType !== VALTYPES.MULTICAT && columnInfo.valueType !== VALTYPES.CATEGORICAL) ||
    !columnInfo.augmentedData.categories
  ) {
    throw getUserError(`categoriesGallery only supported for category columns.`);
  }

  // Identify the categories to include in the gallery.
  let categoryStates: TpCategoryInfo[];
  if (activeFilter) {
    // Consider previous filter steps on the same data table
    const filterSteps = previousFilterSteps.filter(
      (step: TpEnSelectionFilterStep) => !('dataTableId' in step) || step.dataTableId == dataTable.id,
    );
    const canonicalTypeInfo = getCanonicalTypeInfo(ctx.dataContext, dataTable.canonicalTypeId);
    const selectionItemsMap = getSelectionItemsMapForFilter(ctx.dataContext, canonicalTypeInfo, filterSteps);
    categoryStates = getFilteredCategories(dataTable, columnInfo, selectionItemsMap);
  } else {
    // If no filter is applied, we use the categories as defined in the column info
    categoryStates = columnInfo.augmentedData.categories.map(({ value, count }) => {
      return {
        value,
        displayString: convertColumnDataToDisplayString(columnInfo, value),
        count,
        countSelected: 0,
      };
    });
  }
  sortCategories(categoryStates, elemDef.sortBy ?? CATEGORICAL_ORDERS.Count);

  const tabs: TpDashboardTabberDefinition = {
    id: get_guid(),
    elemType: DASHBOARDELEMIDS.Tabber,
    elemLayoutHints: {},
    elements: [],
    tabTitles: [],
  };

  let elemNr = 0;
  let currentTab: TpDashboardVerticalLayoutDefinition;
  let currentRow: TpDashboardHorizontalLayoutDefinition;
  let currentTabElemCount = 0;

  function startNewTab(tabSingleCategoricalState: string | void) {
    // Note: tabSingleCategoricalState should only be defined in case each tab displays a single categorical value
    let tabName = `${elemNr + 1}-`;
    if (!singleChartPerTab) {
      finalizeCurrentTabTitle();
    } else {
      tabName = convertColumnDataToDisplayString(columnInfo, tabSingleCategoricalState);
    }
    const newTab: TpDashboardVerticalLayoutDefinition = {
      id: get_guid(),
      elemType: DASHBOARDELEMIDS.VerticalLayout,
      elemLayoutHints: {},
      elements: [],
    };
    tabs.elements.push(newTab);
    tabs.tabTitles.push(tabName);
    currentTab = newTab;
    currentTabElemCount = 0;
    startNewRow();
  }

  function finalizeCurrentTabTitle() {
    if (tabs.tabTitles.length > 0) tabs.tabTitles[tabs.tabTitles.length - 1] += `${elemNr}`;
  }

  function startNewRow() {
    currentRow = {
      id: get_guid(),
      elemType: DASHBOARDELEMIDS.HorizontalLayout,
      elemLayoutHints: {},
      elements: [],
    };
    currentTab.elements.push(currentRow);
  }

  function addGalleryElement(categoryState: string) {
    if (!singleChartPerTab) {
      if (elemDef.maxCount && currentTabElemCount >= elemDef.maxCount) startNewTab();
      if (currentRow.elements.length >= columnCount) startNewRow();
    } else {
      startNewTab(categoryState);
    }
    const filterStep: TpEnSelectionFilterStepCategorical = {
      type: TpEnSelectionFilterTypes.CATEGORICAL,
      key: `step00${previousFilterSteps.length + 1}_${columnId}`,
      dataTableId: dataTable.id,
      colId: columnId,
      selectedCategoryIdList: [categoryState],
      excludedCategoryIdList: [],
    };
    const filter: TpActiveEnSelectionFilter = {
      steps: [...previousFilterSteps, filterStep],
      manualSelection: false,
    };
    currentRow.elements.push(createElement(ctx, elemDef, elemDef.element, widgets, actions, filter));
    currentTabElemCount++;
    elemNr++;
  }

  if (!singleChartPerTab) startNewTab();

  for (const categoryState of categoryStates) {
    ctx.contextVariables[variable] = categoryState.displayString;
    addGalleryElement(categoryState.value);
    delete ctx.contextVariables[variable];
  }

  if (!singleChartPerTab) finalizeCurrentTabTitle();

  return tabs;
}

/** Resolves action IDs from a template into the runtime action-button definition. */
function createActionButtonsElement(
  ctx: TpDashboardCreationContext,
  parentElemDef: TpContainerElementDTO | null,
  elemDef: Extract<TpSectionElementDTO, { type: typeof ELEMENT_TYPES.ActionButtons }>,
  actions: Record<string, any>,
): TpActionButtonsDefinition {
  const actionDefs: any[] = [];
  for (const actionId of elemDef.actions) {
    if (!actions[actionId]) throw getUserError(`Action not found: ${actionId}`);
    actionDefs.push({
      ...actions[actionId],
      id: actionId,
    });
  }
  return {
    elemType: DASHBOARDELEMIDS.ActionButtons,
    actionDefs,
  };
}

/** Hydrates every top-level template element belonging to a section. */
export function generateElementsForSection(
  ctx: TpDashboardCreationContext,
  section: TpSectionDTO,
  widgets: { [key: string]: TpWidgetDTO },
  actions: { [key: string]: any },
) {
  const dashboardElements: TpDashboardElement[] = section.elements.map((elemDef) =>
    createElement(ctx, null, elemDef, widgets, actions),
  );
  return dashboardElements;
}

/** Adds an empty section to the selected dashboard: currently used only in edit mode. */
function handleAddNewSection(dashboardDef: TpDashboardDefinition, parentId: string, id: string, title: string) {
  const newElement: TpDashboardSectionDefinition = {
    elemType: DASHBOARDELEMIDS.Section,
    id,
    title,
    collapsed: false,
    elements: [],
  };
  const parentElement = findDashboardElement(dashboardDef, parentId).elem;
  if ('elements' in parentElement && Array.isArray(parentElement.elements)) {
    parentElement.elements.push(newElement);
  } else {
    throw new Error(`Dashboard element ${parentElement?.elemType} cannot contain child elements.`);
  }
}

/* Add a new element to a parent dashboard element, but not to the definition itself. */
function addElementToParent(dashboardElement: TpDashboardElement, newElement: TpDashboardElement) {
  if ('elements' in dashboardElement && Array.isArray(dashboardElement.elements)) {
    // Do not directly add to TpDashboardDefinition
    if (dashboardElement.elemType === DASHBOARDELEMIDS.Dashboard) {
      throw new Error(`Cannot add elements directly to the dashboard definition.`);
    }
    dashboardElement.elements.push(newElement);
  } else {
    throw new Error(`Dashboard element ${dashboardElement?.elemType} cannot contain child elements.`);
  }
}

/** Adds an empty horizontal layout with its initial relative width and height hints; currently used only in edit mode. */
function handleAddNewHorizontalLayout(
  dashboardDef: TpDashboardDefinition,
  parentId: string,
  id: string,
  height?: number,
) {
  const newElement: TpDashboardHorizontalLayoutDefinition = {
    id,
    elemType: DASHBOARDELEMIDS.HorizontalLayout,
    elemLayoutHints: getLayoutInHorGroupRelative(DEFAULT_LAYOUT_FRACTION, height),
    elements: [],
  };
  addElementToParent(findDashboardElement(dashboardDef, parentId).elem, newElement);
}

/** Adds an empty vertical layout with its initial width and requested height; currently used only in edit mode. */
function handleAddNewVerticalLayout(
  dashboardDef: TpDashboardDefinition,
  parentId: string,
  id: string,
  height?: number,
) {
  const newElement: TpDashboardVerticalLayoutDefinition = {
    id,
    elemType: DASHBOARDELEMIDS.VerticalLayout,
    elements: [],
    elemLayoutHints: {
      sizeXRelative: DEFAULT_LAYOUT_FRACTION,
      sizeYAbsolute: height || DEFAULT_VERTICAL_LAYOUT_HEIGHT,
    },
  };
  addElementToParent(findDashboardElement(dashboardDef, parentId).elem, newElement);
}

/** Adds a chart widget with the supplied chart type and data binding to a dashboard element; currently used only in edit mode. */
function handleAddNewChart(
  dashboardDef: TpDashboardDefinition,
  parentId: string,
  id: string,
  chartType: string,
  dataBinding: TpChartDataBinding,
) {
  const newElement: TpChartDefinition = {
    elemType: DASHBOARDELEMIDS.Widget,
    id,
    chartType,
    dataBinding,
    config: {},
    elemLayoutHints: getLayoutInHorGroupRelative(DEFAULT_LAYOUT_FRACTION, DEFAULT_CHART_HEIGHT),
  };
  addElementToParent(findDashboardElement(dashboardDef, parentId).elem, newElement);
}

/** Adds a static-text element containing the supplied content; currently used only in edit mode. */
function handleAddNewStaticText(dashboardDef: TpDashboardDefinition, parentId: string, id: string, content: string) {
  const newElement: TpStaticTextDefinition = {
    elemType: DASHBOARDELEMIDS.StaticText,
    id,
    content,
  };
  addElementToParent(findDashboardElement(dashboardDef, parentId).elem, newElement);
}

/** Adds an empty tabber whose tabs and child elements can be configured later; currently used only in edit mode. */
function handleAddNewTabber(dashboardDef: TpDashboardDefinition, parentId: string, id: string) {
  const parentElem = findDashboardElement(dashboardDef, parentId).elem;
  const defaultLayoutHints = getElementLayoutHintsForElement(parentElem, {});
  const newElement: TpDashboardTabberDefinition = {
    elemType: DASHBOARDELEMIDS.Tabber,
    tabTitles: [],
    elements: [],
    id,
    elemLayoutHints: defaultLayoutHints,
  };
  addElementToParent(parentElem, newElement);
}

/** Removes the element with the given ID from its parent's child list; currently used only in edit mode. */
function handleRemoveElement(dashboardDef: TpDashboardDefinition, id: string) {
  const parentId = findDashboardElement(dashboardDef, id).parentId;
  if (!parentId) {
    throw getUserError(`Cannot remove element with id ${id} because it has no parent.`);
  }
  const parentElem = findDashboardElement(dashboardDef, parentId).elem;
  if (!('elements' in parentElem) || !Array.isArray(parentElem.elements)) {
    throw new Error(`Parent element with id ${parentId} has no child elements.`);
  }
  parentElem.elements = parentElem.elements.filter((childElem) => 'id' in childElem && childElem.id != id);
}

/** Moves an element one position backward or forward within its parent's child list; currently used only in edit mode. */
function handleMoveElement(dashboardDef: TpDashboardDefinition, id: string, direction: number) {
  const parentId = findDashboardElement(dashboardDef, id).parentId;
  if (!parentId) {
    throw getUserError(`Cannot move element with id ${id} because it has no parent.`);
  }
  const parentElem = findDashboardElement(dashboardDef, parentId).elem;
  if (!('elements' in parentElem) || !Array.isArray(parentElem.elements)) {
    throw getUserError(`Parent element with id ${parentId} has no child elements.`);
  }
  const elementIndex = parentElem.elements.map((el: any) => el.id).indexOf(id);
  if (direction < 0 && elementIndex > 0) swapArrayElements(parentElem.elements, elementIndex - 1, elementIndex);
  if (direction > 0 && elementIndex < parentElem.elements.length - 1)
    swapArrayElements(parentElem.elements, elementIndex, elementIndex + 1);
}

/** Replaces an element's layout hints rather than merging them with the existing hints; currently used only in edit mode. */
function handleUpdateElementLayoutHints(
  dashboardDef: TpDashboardDefinition,
  id: string,
  newLayoutHints: TpElementLayoutHints,
) {
  // Fully replace the existing layout hints with the new ones, do not merge.
  const elem = findDashboardElement(dashboardDef, id).elem;
  if (supportsElemLayoutHints(elem)) {
    elem.elemLayoutHints = { ...newLayoutHints };
    return;
  }
  throw getUserError(`Element with id ${id} does not support layout hints.`);
}

/** Toggles the collapsed state of a section. */
function handleToggleSectionCollapse(dashboardDef: TpDashboardDefinition, id: string) {
  const elem = findDashboardElement(dashboardDef, id).elem;
  if (!('collapsed' in elem)) {
    throw getUserError(`Element with id ${id} has no collapsed state.`);
  }
  elem.collapsed = !elem.collapsed;
}

/** Updates the title of a dashboard section; currently used only in edit mode. */
function handleUpdateSectionSettings(dashboardDef: TpDashboardDefinition, id: string, title: string) {
  const elem = findDashboardElement(dashboardDef, id).elem;
  if (!('title' in elem)) {
    throw getUserError(`Element with id ${id} has no title.`);
  }
  elem.title = title;
}

/** Replaces the content of a static-text element; currently used only in edit mode. */
function handleUpdateStaticTextSettings(dashboardDef: TpDashboardDefinition, id: string, content: string) {
  const elem = findDashboardElement(dashboardDef, id).elem;
  if (!('content' in elem)) {
    throw getUserError(`Element with id ${id} has no content.`);
  }
  elem.content = content;
}

/** Replaces the titles displayed for a tabber's tabs; currently used only in edit mode. */
function handleUpdateTabberSettings(dashboardDef: TpDashboardDefinition, id: string, tabTitles: string[]) {
  const elem = findDashboardElement(dashboardDef, id).elem;
  if (!('tabTitles' in elem)) {
    throw getUserError(`Element with id ${id} has no tab titles.`);
  }
  elem.tabTitles = tabTitles;
}

// Get the section based on ID or fall back to the last section
function getTargetSection(dashboardDef: TpDashboardDefinition, targetSectionId: string): TpDashboardSectionDefinition {
  let targetSection = null;
  for (const el of dashboardDef.elements)
    if (el.id == targetSectionId && el.elemType == DASHBOARDELEMIDS.Section) targetSection = el;
  if (!targetSection) targetSection = dashboardDef.elements[dashboardDef.elements.length - 1];
  return targetSection;
}

function getOrCreateTargetSequenceLayout(
  targetSection: TpDashboardSectionDefinition,
  ctx: TpDashboardCreationContext | null,
): TpDashboardSequenceLayoutDefinition {
  const lastElement = targetSection.elements[targetSection.elements.length - 1];

  // 1. Return existing SequenceLayout (type narrowed by guard)
  if (isSequenceLayoutElement(lastElement)) {
    return lastElement;
  }

  // 2. Create, append, and return new SequenceLayout (natively typed)
  const newSequenceLayout = createSequenceLayout(ctx, get_guid(), []);
  targetSection.elements.push(newSequenceLayout);

  return newSequenceLayout;
}

/** Appends an element to the section's trailing sequence layout. Element can be created by the user through actions or other means. */
function handleAddNewSectionElement(
  dashboardDef: TpDashboardDefinition,
  elementDef: TpSectionElementDTO,
  targetSectionId: string,
  ctx: TpDashboardUpdateContext,
) {
  const dataContext = analyticsDataStorage.dataContexts[ctx.dataContextId];
  if (!dataContext) throw getUserError(`Invalid data context: ${ctx.dataContextId}`);
  const fullCtx = { dataContext: dataContext, contextVariables: ctx.contextVariables };

  const targetSection = getTargetSection(dashboardDef, targetSectionId);
  const targetSequenceElement = getOrCreateTargetSequenceLayout(targetSection, fullCtx);

  // CreateElement requires a parentDefElement, convert the sequence
  const parentDefElement: TpContainerElementDTO = {
    type: ELEMENT_TYPES.LayoutSequence,
    layoutProperties: getLayoutPropertiesFromLayoutHints(targetSequenceElement.elemLayoutHints ?? {}),
    elements: [],
  };

  const element = createElement(fullCtx, parentDefElement, elementDef, {}, {});
  targetSequenceElement.elements.push({
    ...element,
  });
}

/** Appends a user-created chart and marks it removable by the user. */
function handleAddNewChartByUser(
  dashboardDef: TpDashboardDefinition,
  chartDef: TpChartDefinition,
  targetSectionId: string,
) {
  const targetSection = getTargetSection(dashboardDef, targetSectionId);
  const targetSequenceElement = getOrCreateTargetSequenceLayout(targetSection, null);
  targetSequenceElement.elements.push({
    ...chartDef,
    config: {
      ...chartDef.config,
      userCanRemove: true,
    },
  });
}

/** Updates or removes a chart's primary or secondary aspect binding. */
function handleUpdateChartDataBinding(
  dashboardDef: TpDashboardDefinition,
  chartId: string,
  aspectId: string,
  columnId: string,
  onSecondaryTable: boolean,
) {
  const chartElem = findDashboardElement(dashboardDef, chartId).elem;
  if (!isChartDefinition(chartElem)) throw getUserError(`Element with ID ${chartId} is not a chart`);
  if (!onSecondaryTable) {
    if (!columnId || columnId == UNBOUND_COLUMN) delete chartElem.dataBinding.aspectBindings[aspectId];
    else chartElem.dataBinding.aspectBindings[aspectId] = { columnId };
  } else {
    if (!chartElem.dataBinding.secondAspectBindings)
      throw getUserError(`Chart with ID ${chartId} has no secondary aspect bindings.`);
    if (!columnId || columnId == UNBOUND_COLUMN) delete chartElem.dataBinding.secondAspectBindings[aspectId];
    else chartElem.dataBinding.secondAspectBindings[aspectId] = { columnId };
  }
}

/** Replaces a chart's compound aspect binding with the supplied column IDs. */
function handleUpdateChartCompoundDataBinding(
  dashboardDef: TpDashboardDefinition,
  chartId: string,
  aspectId: string,
  columnIds: string[],
) {
  const chartElem = findDashboardElement(dashboardDef, chartId).elem;
  if (!isChartDefinition(chartElem)) throw getUserError(`Element with ID ${chartId} is not a chart`);
  if (!chartElem.dataBinding.compoundAspectBindings)
    throw getUserError(`Chart with ID ${chartId} has no compound aspect bindings.`);
  chartElem.dataBinding.compoundAspectBindings[aspectId] = columnIds;
}

/** Updates one chart configuration parameter (for example through actions). */
function handleUpdateChartConfigParam(
  dashboardDef: TpDashboardDefinition,
  chartId: string,
  configParamId: string,
  value: any,
) {
  const chartElem = findDashboardElement(dashboardDef, chartId).elem;
  if (!isChartDefinition(chartElem)) throw getUserError(`Element with ID ${chartId} is not a chart`);
  chartElem.config[configParamId] = value;
}

/** Sets a chart filter, removing the filter when it contains no steps. */
function handleUpdateChartFilter(
  dashboardDef: TpDashboardDefinition,
  chartId: string,
  newFilter: TpActiveEnSelectionFilter,
) {
  const chartElem = findDashboardElement(dashboardDef, chartId).elem;
  if (!isChartDefinition(chartElem)) throw getUserError(`Element with ID ${chartId} is not a chart`);
  if (newFilter.steps && newFilter.steps.length > 0) chartElem.dataBinding.filter = newFilter;
  else delete chartElem.dataBinding.filter; // no filter specified
}

/** Updates one scalar chart aspect and rejects unknown aspect IDs. */
function handleUpdateChartAspectBinding(
  dashboardDef: TpDashboardDefinition,
  chartId: string,
  aspectId: string,
  newColId: string,
) {
  const chartElem = findDashboardElement(dashboardDef, chartId).elem;
  if (!isChartDefinition(chartElem)) throw getUserError(`Element with ID ${chartId} is not a chart`);
  const aspectBindings = chartElem.dataBinding.aspectBindings;
  if (!aspectBindings[aspectId]) throw getUserError(`Invalid aspect ID ${aspectId}`);
  aspectBindings[aspectId].columnId = newColId;
}

/** Applies a clear, replace, add, or add-if-absent operation to a compound aspect. */
function handleUpdateChartCompoundAspectBinding(
  dashboardDef: TpDashboardDefinition,
  chartId: string,
  compoundAspectId: string,
  newColId: string,
  method: string,
) {
  const chartElem = findDashboardElement(dashboardDef, chartId).elem;
  if (!isChartDefinition(chartElem)) throw getUserError(`Element with ID ${chartId} is not a chart`);
  const compoundAspectBindings = chartElem.dataBinding.compoundAspectBindings;
  if (!compoundAspectBindings) throw getUserError(`Chart with ID ${chartId} has no compound aspect bindings.`);
  if (!compoundAspectBindings[compoundAspectId]) throw getUserError(`Invalid compound aspect ID ${compoundAspectId}`);
  if (method == 'clear') {
    compoundAspectBindings[compoundAspectId] = [];
    return;
  }
  if (method == 'replace') {
    compoundAspectBindings[compoundAspectId] = [newColId];
    return;
  }
  if (method == 'add') {
    compoundAspectBindings[compoundAspectId].push(newColId);
    return;
  }
  if (method == 'addIfAbsent') {
    if (!compoundAspectBindings[compoundAspectId].includes(newColId))
      compoundAspectBindings[compoundAspectId].push(newColId);
    return;
  }
  throw `Invalid method for compound chart aspect modification: ${method}`;
}

export const dashboardDefsDataSlice = createSlice({
  name: 'dashboardDefs',
  initialState,
  reducers: {
    addDashboard: (state, action: PayloadAction<{ dashboardDef: TpDashboardDefinition }>) => {
      handleAddNewDashboard(state, action.payload.dashboardDef);
    },
    removeDashboard: (state, action: PayloadAction<string>) => {
      handleRemoveDashboard(state, action.payload);
    },

    addNewSection: (
      state,
      action: PayloadAction<{ dashboardDefId: string; parentId: string; id: string; title: string }>,
    ) => {
      const { parentId } = action.payload;
      if (parentId !== '') {
        console.warn(`Adding a new section with a non-empty parentId: ${parentId}. Sections should be added to root`);
      }
      handleAddNewSection(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.parentId,
        action.payload.id,
        action.payload.title,
      );
    },

    addNewHorizontalLayout: (
      state,
      action: PayloadAction<{ dashboardDefId: string; parentId: string; id: string; height?: number }>,
    ) => {
      const { height } = action.payload;
      if (height !== undefined && height !== 0) {
        console.warn(`Adding a new horizontal layout with a non-zero height: ${height}. Currently not fully supported`);
      }

      handleAddNewHorizontalLayout(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.parentId,
        action.payload.id,
        action.payload.height,
      );
    },

    addNewVerticalLayout: (
      state,
      action: PayloadAction<{ dashboardDefId: string; parentId: string; id: string; height?: number }>,
    ) => {
      handleAddNewVerticalLayout(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.parentId,
        action.payload.id,
        action.payload.height,
      );
    },

    addNewChart: (
      state,
      action: PayloadAction<{
        dashboardDefId: string;
        parentId: string;
        id: string;
        chartType: string;
        dataBinding: TpChartDataBinding;
      }>,
    ) => {
      handleAddNewChart(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.parentId,
        action.payload.id,
        action.payload.chartType,
        action.payload.dataBinding,
      );
    },

    addNewStaticText: (
      state,
      action: PayloadAction<{ dashboardDefId: string; parentId: string; id: string; content: string }>,
    ) => {
      handleAddNewStaticText(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.parentId,
        action.payload.id,
        action.payload.content,
      );
    },

    addNewTabber: (state, action: PayloadAction<{ dashboardDefId: string; parentId: string; id: string }>) => {
      handleAddNewTabber(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.parentId,
        action.payload.id,
      );
    },

    addNewElementToSection: (
      state,
      action: PayloadAction<{
        dashboardDefId: string;
        element: TpSectionElementDTO;
        targetSectionId: string;
        ctx: TpDashboardUpdateContext;
      }>,
    ) => {
      handleAddNewSectionElement(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.element,
        action.payload.targetSectionId,
        action.payload.ctx,
      );
    },

    addNewChartByUser: (
      state,
      action: PayloadAction<{ dashboardDefId: string; chartDef: TpChartDefinition; targetSectionId: string }>,
    ) => {
      handleAddNewChartByUser(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.chartDef,
        action.payload.targetSectionId,
      );
    },

    updateElementLayoutHints: (
      state,
      action: PayloadAction<{ dashboardDefId: string; id: string; newLayoutHints: TpElementLayoutHints }>,
    ) => {
      handleUpdateElementLayoutHints(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.id,
        action.payload.newLayoutHints,
      );
    },

    updateSectionSettings: (state, action: PayloadAction<{ dashboardDefId: string; id: string; title: string }>) => {
      handleUpdateSectionSettings(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.id,
        action.payload.title,
      );
    },

    toggleSectionCollapse: (state, action: PayloadAction<{ dashboardDefId: string; id: string }>) => {
      handleToggleSectionCollapse(getDashboardDef(state, action.payload.dashboardDefId), action.payload.id);
    },

    updateStaticTextSettings: (
      state,
      action: PayloadAction<{ dashboardDefId: string; id: string; content: string }>,
    ) => {
      handleUpdateStaticTextSettings(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.id,
        action.payload.content,
      );
    },

    updateTabberSettings: (
      state,
      action: PayloadAction<{ dashboardDefId: string; id: string; tabTitles: string[] }>,
    ) => {
      handleUpdateTabberSettings(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.id,
        action.payload.tabTitles,
      );
    },

    removeElement: (state, action: PayloadAction<{ dashboardDefId: string; id: string }>) => {
      handleRemoveElement(getDashboardDef(state, action.payload.dashboardDefId), action.payload.id);
    },

    moveElement: (state, action: PayloadAction<{ dashboardDefId: string; id: string; direction: number }>) => {
      handleMoveElement(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.id,
        action.payload.direction,
      );
    },

    updateChartDataBinding: (
      state,
      action: PayloadAction<{
        dashboardDefId: string;
        chartId: string;
        aspectId: string;
        columnId: string;
        onSecondaryTable: boolean;
      }>,
    ) => {
      handleUpdateChartDataBinding(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.chartId,
        action.payload.aspectId,
        action.payload.columnId,
        action.payload.onSecondaryTable,
      );
    },

    updateChartCompoundDataBinding: (
      state,
      action: PayloadAction<{
        dashboardDefId: string;
        chartId: string;
        aspectId: string;
        columnIds: string[];
      }>,
    ) => {
      handleUpdateChartCompoundDataBinding(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.chartId,
        action.payload.aspectId,
        action.payload.columnIds,
      );
    },

    updateChartConfigParam: (
      state,
      action: PayloadAction<{ dashboardDefId: string; chartId: string; configParamId: string; value: any }>,
    ) => {
      handleUpdateChartConfigParam(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.chartId,
        action.payload.configParamId,
        action.payload.value,
      );
    },

    updateChartFilter: (
      state,
      action: PayloadAction<{ dashboardDefId: string; chartId: string; newFilter: TpActiveEnSelectionFilter }>,
    ) => {
      handleUpdateChartFilter(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.chartId,
        action.payload.newFilter,
      );
    },

    updateChartAspectBinding: (
      state,
      action: PayloadAction<{ dashboardDefId: string; chartId: string; aspectId: string; newColId: string }>,
    ) => {
      handleUpdateChartAspectBinding(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.chartId,
        action.payload.aspectId,
        action.payload.newColId,
      );
    },

    updateChartCompoundAspectBinding: (
      state,
      action: PayloadAction<{
        dashboardDefId: string;
        chartId: string;
        compoundAspectId: string;
        newColId: string;
        method: string;
      }>,
    ) => {
      handleUpdateChartCompoundAspectBinding(
        getDashboardDef(state, action.payload.dashboardDefId),
        action.payload.chartId,
        action.payload.compoundAspectId,
        action.payload.newColId,
        action.payload.method,
      );
    },

    setInitialDashboardState: (state) => {
      state.dashboardDefs = {};
    },
  },
});

export const {
  addDashboard,
  removeDashboard,
  removeElement,
  moveElement,
  addNewChartByUser,
  addNewElementToSection,
  addNewSection,
  addNewHorizontalLayout,
  addNewVerticalLayout,
  addNewChart,
  updateChartAspectBinding,
  updateChartCompoundAspectBinding,
  updateChartFilter,
  addNewStaticText,
  addNewTabber,
  updateElementLayoutHints,
  updateSectionSettings,
  updateStaticTextSettings,
  updateTabberSettings,
  updateChartDataBinding,
  updateChartCompoundDataBinding,
  updateChartConfigParam,
  toggleSectionCollapse,
  setInitialDashboardState,
} = dashboardDefsDataSlice.actions;

export const selectDashboardDefs = (state: RootState) => state.dashboardDefs;

export default dashboardDefsDataSlice.reducer;
